import { cva } from "class-variance-authority";

// No React or Base UI here: a server-rendered page imports this without the button's client code.
export const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-full border border-transparent bg-clip-padding font-semibold whitespace-nowrap outline-none select-none transition-[background-color,box-shadow,scale,translate,color] duration-[var(--duration-fast)] ease-ui focus-visible:focus-ring aria-busy:active:translate-y-0 aria-busy:active:scale-100 disabled:pointer-events-none not-aria-busy:disabled:opacity-50 not-aria-busy:aria-disabled:opacity-50 disabled:shadow-none aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-[var(--icon-size)]",
  {
    variants: {
      variant: {
        primary:
          "border-primary-edge bg-linear-to-b from-primary-from to-primary-to text-primary-foreground hover:from-primary-hover-from hover:to-primary-hover-to active:scale-[0.98] active:from-primary-pressed-from active:to-primary-pressed-to active:shadow-pressed-fill",
        // A surface, not a grey fill: with one filled button per screen, a grey secondary reads as
        // disabled.
        secondary:
          "border-edge bg-linear-to-b from-secondary-from to-secondary-to text-foreground-soft shadow-lift-1 hover:shadow-raised active:scale-[0.98] active:border-transparent active:bg-muted active:bg-none active:shadow-pressed",
        ghost:
          "text-muted-foreground hover:bg-muted hover:text-foreground active:not-aria-[haspopup]:translate-y-px aria-expanded:bg-muted aria-expanded:text-foreground",
        // The final button of a dialog that takes something away (`docs/spec/people.md`, destructive
        // actions); the label on the fill is the pair the danger hover is measured with.
        destructive:
          "border-destructive bg-destructive text-destructive-foreground active:scale-[0.98] active:shadow-pressed-fill focus-visible:ring-destructive",
        // No resting tint: a 10% fill drops the --destructive label from 4.76:1 to 3.99:1 in light
        // mode, under the text floor. The hover fill carries --destructive-foreground as its label.
        danger:
          "border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground active:not-aria-[haspopup]:translate-y-px focus-visible:ring-destructive",
        // A destructive action resting in a settings row; the dialog it opens ends in `destructive`.
        "danger-soft":
          "bg-tint-red text-destructive hover:bg-destructive hover:text-destructive-foreground active:not-aria-[haspopup]:translate-y-px focus-visible:ring-destructive",
        // The client's invoice page: a page without the app's chrome draws its one action in ink.
        ink: "rounded-row bg-foreground text-card hover:bg-foreground-soft active:translate-y-px",
        // A text action: a standalone one still keeps its size's target.
        link: "text-primary-text underline-offset-4 hover:underline",
      },
      size: {
        // Table rows and toolbars only: 36px on touch, 32px on a fine pointer, both over the 24px
        // AA target floor. The label stays at body size.
        sm: "h-[var(--control-h-sm)] min-h-[var(--control-h-sm)] gap-1.5 px-[var(--control-px-sm)] text-body",
        md: "h-[var(--size-touch)] min-h-[var(--size-touch)] gap-2 px-[var(--control-px)] text-body",
        lg: "h-[var(--control-h-lg)] min-h-[var(--control-h-lg)] gap-2 px-[var(--control-px)] text-body",
        icon: "size-[var(--size-touch)] min-h-[var(--size-touch)]",
        "icon-sm": "size-[var(--control-h-sm)] min-h-[var(--control-h-sm)]",
      },
    },
    compoundVariants: [{ variant: "link", class: "px-0" }],
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);
